<template>
  <div>
    <div class="row">
      <div class="col-xl-6">
        <single-select-person
          :id="formId + '-person'"
          :person="reservation.person"
          :label="$t('global.person')"
          :allowOpen="!!reservationId"
          :editable="editable"
          :required="true"
          @confirmed="setPerson"
        />
      </div>
      <div class="col-xl-6">
        <div class="form-group">
          <v-select
            v-model="reservation.reservationstyp"
            label="bezeichnung"
            :options="reservationstypen"
            :reduce="(t) => t.id"
            :disabled="!editable"
            :placeholder="$t('global.reservationtype')"
          >
            <span slot="no-options">{{ $t("global.taptosearch") }}</span>
          </v-select>
          <label :class="{ 'text-danger': !reservation.reservationstyp }">{{ $t("global.reservationtype") }}</label>
        </div>
      </div>
      <div v-if="lehrgang" class="col-xl-6">
        <div class="form-group">
          <v-select
            v-model="modul"
            label="bezeichnung"
            :options="module"
            :reduce="(m) => m.id"
            :disabled="!editable"
            :clearable="false"
            :placeholder="$t('global.module')"
            @input="modulGewechselt"
          >
            <span slot="no-options">{{ $t("global.taptosearch") }}</span>
          </v-select>
          <label :class="{ 'text-danger': !modul }">{{ $t("global.module") }}</label>
        </div>
      </div>
      <div class="col-xl-6">
        <div class="form-group">
          <v-select
            v-model="reservation.zimmer"
            :getOptionLabel="kontingentLabel"
            :options="passendeKontingente"
            :reduce="(k) => k.id"
            :disabled="!editable"
            :placeholder="$t('global.hotelopen')"
          >
            <span slot="no-options">{{ $t("global.taptosearch") }}</span>
          </v-select>
          <label>{{ $t("global.hotel") }}</label>
        </div>
      </div>
      <div class="col-xl-6 d-flex align-items-center mb-3">
        <b-form-checkbox v-model="reservation.voranreise" :disabled="!editable">
          {{ $t("global.arrivaltheeveningbefore") }}
        </b-form-checkbox>
      </div>
    </div>

    <div class="row">
      <div class="col-xl-12">
        <b-table
          small
          responsive
          show-empty
          sticky-header="350px"
          :items="termine"
          :fields="terminFields"
          :busy="termineLaden"
        >
          <template v-slot:head(selected)>
            <b-form-checkbox
              :checked="alleTermineGewaehlt"
              :disabled="!editable"
              @change="alleTermineWaehlen"
            ></b-form-checkbox>
          </template>
          <template v-slot:cell(selected)="row">
            <b-form-checkbox v-model="gewaehlteTermine" :value="row.item.id" :disabled="!editable"></b-form-checkbox>
          </template>
          <template #table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
            </div>
          </template>
          <template #empty>
            <div class="text-center text-primary my-2">
              <strong>{{ $t("global.nodataavailable") }}</strong>
            </div>
          </template>
        </b-table>
      </div>
    </div>
  </div>
</template>

<script>
import AppApi from "@/api/AppApi";
import SingleSelectPerson from "@/components/global/SingleSelectPerson";
import { apiErrorToAlert } from "@/utils/Errorhandler";

/**
 * Formular einer Zimmerreservation im Kontext einer Kurzveranstaltung (veranstaltung) oder eines Lehrgangs (lehrgang).
 * Beim Lehrgang wird zuerst das Modul gewählt; daraus ergeben sich die auswählbaren Termine und Hotels.
 * Verwendet im ZimmerreservationModal und auf der Maske Zimmerreservation.
 *
 * API (über ref): initialisieren(reservationId | null), speichern() → Promise mit der gespeicherten Reservation.
 * Event «valid» (Boolean): ob gespeichert werden kann.
 */
export default {
  name: "ZimmerreservationFormular",
  components: { SingleSelectPerson },
  props: {
    formId: { type: String, default: "zimmerreservation-formular" },
    veranstaltung: { type: String, required: false },
    lehrgang: { type: String, required: false },
    module: { type: Array, default: () => [] },
    hotelkontingente: { type: Array, default: () => [] },
    reservationstypen: { type: Array, default: () => [] },
    editable: { type: Boolean, required: true },
    // Hotelkontingent, das bei neuen Reservationen vorausgewählt wird (Maske Hotelkontingent)
    vorgabeKontingent: { type: Object, required: false },
  },
  data() {
    return {
      reservationId: null,
      reservation: this.leereReservation(),
      modul: null,
      termine: [],
      gewaehlteTermine: [],
      termineLaden: false,
    };
  },
  computed: {
    terminFields() {
      return [
        { key: "selected", label: "" },
        { key: "von", label: this.$t("global.from") },
        { key: "bis", label: this.$t("global.till") },
        { key: "bezeichnung", label: this.$t("global.appointment") },
        // Lehrgang: Termine hängen an den Fächern; Kurzveranstaltung: an der Veranstaltung selbst
        { key: "veranstaltung.bezeichnung", label: this.lehrgang ? this.$t("global.Subject") : this.$t("global.event") },
      ];
    },
    passendeKontingente() {
      if (!this.lehrgang) return this.hotelkontingente;
      return this.hotelkontingente.filter((k) => !k.veranstaltung || k.veranstaltung.id === this.modul);
    },
    alleTermineGewaehlt() {
      return this.termine.length > 0 && this.gewaehlteTermine.length === this.termine.length;
    },
    valid() {
      return !!(this.reservation.person?.id && this.reservation.reservationstyp && this.gewaehlteTermine.length > 0);
    },
  },
  watch: {
    valid: {
      immediate: true,
      handler(val) {
        this.$emit("valid", val);
      },
    },
  },
  methods: {
    leereReservation() {
      return { person: null, reservationstyp: null, zimmer: null, voranreise: false };
    },
    kontingentLabel(kontingent) {
      const modul = this.lehrgang
        ? ` (${kontingent.veranstaltung ? kontingent.veranstaltung.bezeichnung : this.$t("global.allmodules")})`
        : "";
      return `${kontingent.gebaeude?.bezeichnung || ""}${modul}`;
    },
    initialisieren(reservationId) {
      this.reservationId = reservationId || null;
      this.reservation = this.leereReservation();
      this.gewaehlteTermine = [];
      this.termine = [];
      this.modul = this.lehrgang && this.module.length === 1 ? this.module[0].id : null;

      if (!this.reservationId) {
        if (this.vorgabeKontingent) {
          this.reservation.zimmer = this.vorgabeKontingent.id;
          if (this.lehrgang && this.vorgabeKontingent.veranstaltung) this.modul = this.vorgabeKontingent.veranstaltung.id;
        }
        this.ladeTermine(null);
        return Promise.resolve(null);
      }

      return AppApi.get("zimmerreservation/", { params: { id: this.reservationId } })
        .then((response) => {
          const r = response.data.reservation;
          this.reservation = {
            person: r.person ? { id: r.person.id, personName: r.person.bezeichnung } : null,
            reservationstyp: r.reservationstyp?.id || null,
            zimmer: r.zimmer,
            voranreise: r.voranreise,
          };
          this.modul = this.lehrgang ? r.veranstaltung?.id || null : null;
          this.ladeTermine(response.data.termine.map((t) => t.id));
          return response.data;
        })
        .catch((e) => {
          this.$notify(apiErrorToAlert(e));
          return null;
        });
    },
    modulGewechselt() {
      if (this.reservation.zimmer && !this.passendeKontingente.some((k) => k.id === this.reservation.zimmer)) {
        this.reservation.zimmer = null;
      }
      this.ladeTermine(null);
    },
    /**
     * @param vorauswahl Termin-IDs der bestehenden Reservation; null = alle Termine vorauswählen
     */
    ladeTermine(vorauswahl) {
      let params;
      if (this.lehrgang) {
        if (!this.modul) return;
        params = { modul: this.modul };
      } else if (this.veranstaltung) {
        params = { veranstaltung: this.veranstaltung };
      } else {
        return;
      }

      this.termineLaden = true;
      AppApi.get("zimmerreservation-termine/", { params })
        .then((response) => {
          this.termine = response.data || [];
          const ids = this.termine.map((t) => t.id);
          this.gewaehlteTermine = vorauswahl ? vorauswahl.filter((id) => ids.includes(id)) : ids;
        })
        .catch((e) => this.$notify(apiErrorToAlert(e)))
        .finally(() => {
          this.termineLaden = false;
        });
    },
    alleTermineWaehlen(checked) {
      this.gewaehlteTermine = checked ? this.termine.map((t) => t.id) : [];
    },
    setPerson(person) {
      this.reservation.person = person;
    },
    speichern() {
      const json = {
        person: this.reservation.person?.id,
        reservationstyp: this.reservation.reservationstyp,
        zimmer: this.reservation.zimmer || null,
        voranreise: this.reservation.voranreise,
        termine: this.gewaehlteTermine,
      };

      const request = this.reservationId
        ? AppApi.put("zimmerreservation/", json, { params: { id: this.reservationId } })
        : AppApi.post("zimmerreservation/", json);

      return request.then((response) => {
        this.$notify({
          type: "success",
          title: this.$t("global.actionsuccessful"),
          text: this.$t("global.roomreservationsavedsuccessfully"),
        });
        return response.data;
      });
    },
  },
};
</script>
